/**
 * API client.
 *
 * Same-origin: CloudFront forwards /api/* to the HTTP API, so there is no base
 * URL to configure and no CORS to negotiate. In `vite dev` the same paths are
 * proxied (see vite.config.ts).
 */

export interface ApiError {
  error: string;
  /** Present on a 422 from the import endpoint. */
  issues?: { id: string | null; message: string }[];
  status: number;
}

export class ApiFailure extends Error {
  readonly status: number;
  readonly issues?: { id: string | null; message: string }[];

  constructor(body: ApiError) {
    super(body.error);
    this.name = 'ApiFailure';
    this.status = body.status;
    this.issues = body.issues;
  }
}

const TOKEN_KEY = 'examatron.instructor';

interface StoredToken {
  token: string;
  /** Unix seconds. */
  expiresAt: number;
}

export function loadToken(): string | null {
  const raw = localStorage.getItem(TOKEN_KEY);
  if (!raw) return null;
  try {
    const stored = JSON.parse(raw) as StoredToken;
    // Checked here as well as on the server so an expired token shows the sign-in
    // screen immediately rather than after a failed request.
    if (stored.expiresAt <= Math.floor(Date.now() / 1000)) {
      localStorage.removeItem(TOKEN_KEY);
      return null;
    }
    return stored.token;
  } catch {
    localStorage.removeItem(TOKEN_KEY);
    return null;
  }
}

export const saveToken = (t: StoredToken) => localStorage.setItem(TOKEN_KEY, JSON.stringify(t));
export const clearToken = () => localStorage.removeItem(TOKEN_KEY);

async function request<T>(
  path: string,
  init: RequestInit & { auth?: boolean } = {},
): Promise<T> {
  const headers = new Headers(init.headers);
  if (init.body) headers.set('content-type', 'application/json');

  if (init.auth) {
    const token = loadToken();
    if (!token) throw new ApiFailure({ error: 'Not signed in.', status: 401 });
    headers.set('authorization', `Bearer ${token}`);
  }

  const res = await fetch(path, { ...init, headers });
  const text = await res.text();
  const body = text ? (JSON.parse(text) as Record<string, unknown>) : {};

  if (!res.ok) {
    if (res.status === 401) clearToken();
    throw new ApiFailure({
      error: String(body.error ?? `Request failed (${res.status}).`),
      issues: body.issues as ApiError['issues'],
      status: res.status,
    });
  }
  return body as T;
}

// --------------------------------------------------------------- endpoints

export const health = () => request<{ ok: boolean; configured: boolean }>('/api/health');

export const login = (password: string) =>
  request<StoredToken>('/api/admin/login', {
    method: 'POST',
    body: JSON.stringify({ password }),
  });

export const checkSession = () => request<{ role: string }>('/api/admin/session', { auth: true });

export interface QuestionSetSummary {
  qsetId: string;
  examNumber: number;
  examName: string;
  total: number;
  domains: { number: number; name: string; count: number }[];
  updatedAt: string;
}

export const listSets = () =>
  request<{ sets: QuestionSetSummary[] }>('/api/admin/qsets', { auth: true });

export interface ImportResult {
  qsetId: string;
  replaced: boolean;
  questions: number;
  questionsRemoved: number;
  domains: { number: number; count: number }[];
}

export const importSet = (qsetId: string, set: unknown) =>
  request<ImportResult>('/api/admin/qsets', {
    method: 'POST',
    auth: true,
    body: JSON.stringify({ qsetId, set }),
  });

export interface SessionSummary {
  sessionId: string;
  code: string;
  name: string;
  qsetId: string;
  status: 'CREATED' | 'ACTIVE' | 'PAUSED' | 'COMPLETED';
  createdAt: string;
}

export const listSessions = () =>
  request<{ sessions: SessionSummary[] }>('/api/admin/sessions', { auth: true });

export const createSession = (input: {
  qsetId: string;
  name?: string;
  autoRevealJustification?: boolean;
}) =>
  request<SessionSummary>('/api/admin/sessions', {
    method: 'POST',
    auth: true,
    body: JSON.stringify(input),
  });
